import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Warning, ClipboardText, Plus, X, Printer, ArrowRight, ListChecks } from '@phosphor-icons/react';
import api from '../api';
import SmartPicker from '../components/SmartPicker';
import WorkflowActions from '../components/WorkflowActions';
import DraftRestoreBanner from '../components/DraftRestoreBanner';
import { useWorkflowDraft } from '../hooks/useWorkflowDraft';
import { useHighlight } from '../hooks/useHighlight';
import { useAuth } from '../contexts/AuthContext';

const ROOT_CAUSE_CATS = ['Process', 'Material', 'Machine', 'Method', 'Manpower', 'Measurement'];
const CAPA_STATUSES = { open: 'hsl(0,84%,60%)', in_progress: 'hsl(43,96%,56%)', closed: 'hsl(142,70%,45%)' };

function printQCCOA(rec) {
  const win = window.open('', '_blank', 'width=820,height=960');
  const params = (rec.parameters || []).map(p =>
    `<tr><td>${p.name}</td><td style="text-align:center">${p.target}</td>
     <td style="text-align:center">${p.actual}</td>
     <td style="text-align:center"><span style="font-weight:700;color:${p.status==='pass'?'#197b30':'#b91c1c'}">${p.status?.toUpperCase()}</span></td></tr>`
  ).join('');
  const statusColor = rec.overall_status === 'pass' ? '#197b30' : rec.overall_status === 'hold' ? '#b45309' : '#b91c1c';
  win.document.write(`<!DOCTYPE html><html><head><title>QC COA ${rec.batch_no}</title>
<style>
  body{font-family:monospace;padding:40px;color:#111;font-size:13px}
  .hdr{text-align:center;border-bottom:3px solid #111;padding-bottom:16px;margin-bottom:24px}
  h1{font-size:24px;letter-spacing:.2em;margin:0;text-transform:uppercase}
  h2{font-size:13px;margin:4px 0;color:#555;text-transform:uppercase}
  .row{display:flex;justify-content:space-between;gap:24px;margin-bottom:20px}
  .lbl{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.1em;margin-bottom:4px}
  .verdict{font-size:20px;font-weight:700;color:${statusColor};text-transform:uppercase;letter-spacing:.1em}
  table{width:100%;border-collapse:collapse;margin:16px 0}
  th,td{border:1px solid #ccc;padding:8px 12px;font-size:12px}
  th{background:#f5f5f5;font-size:11px;text-transform:uppercase}
  .footer{margin-top:40px;font-size:10px;color:#aaa;border-top:1px solid #ddd;padding-top:12px;text-align:center}
  @media print{@page{margin:18mm}}
</style></head><body>
<div class="hdr"><h1>ARIA FORGE ERP</h1><h2>Certificate of Analysis (COA)</h2></div>
<div class="row">
  <div>
    <div class="lbl">Batch No</div><div style="font-size:15px;font-weight:700">${rec.batch_no}</div>
    <div class="lbl" style="margin-top:8px">Product</div><div>${rec.product_name || '—'}</div>
    <div class="lbl" style="margin-top:8px">QC Type</div><div>${(rec.qc_type || '').toUpperCase()}</div>
  </div>
  <div>
    <div class="lbl">Inspector</div><div>${rec.inspector || '—'}</div>
    <div class="lbl" style="margin-top:8px">Work Order</div><div>${rec.wo_no || '—'}</div>
    <div class="lbl" style="margin-top:8px">Verdict</div><div class="verdict">${(rec.overall_status || '').toUpperCase()}</div>
  </div>
</div>
<table>
  <thead><tr><th>Parameter</th><th style="text-align:center">Target</th>
  <th style="text-align:center">Actual</th><th style="text-align:center">Result</th></tr></thead>
  <tbody>${params}</tbody>
</table>
${rec.notes ? `<div style="margin-top:16px;padding:12px;border:1px solid #ddd"><div class="lbl">Notes</div><div>${rec.notes}</div></div>` : ''}
<div class="footer">Generated by ARIA FORGE ERP &nbsp;|&nbsp; ${new Date().toLocaleString('en-IN')}</div>
<script>window.onload=()=>window.print()</script>
</body></html>`);
  win.document.close();
}

function StatusChip({ status }) {
  if (status === 'pass') return <span className="chip-active">PASS</span>;
  if (status === 'fail') return <span className="chip-danger">FAIL</span>;
  if (status === 'hold') return <span className="chip-hold">HOLD</span>;
  return <span className="chip-neutral">{status?.toUpperCase()}</span>;
}

function ParamRow({ p }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid hsl(0,0%,9%)' }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,70%)', flex: 2 }}>{p.name}</span>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,45%)', flex: 1, textAlign: 'center' }}>{p.target}</span>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: p.status === 'fail' ? 'hsl(0,84%,60%)' : 'hsl(0,0%,75%)', flex: 1, textAlign: 'center' }}>{p.actual}</span>
      <div style={{ flex: 1, textAlign: 'right' }}><StatusChip status={p.status} /></div>
    </div>
  );
}

const INP = {
  width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
  padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11, outline: 'none', boxSizing: 'border-box',
};
const LBL = {
  display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4,
};

export default function Quality() {
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showCapa, setShowCapa] = useState(false);
  const [capas, setCapas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('detail'); // 'detail' | 'capa'
  const [capaForm, setCapaForm] = useState({
    root_cause_category: 'Process', root_cause_description: '',
    corrective_action: '', preventive_action: '',
    due_date: '', owner: '',
  });
  const [form, setForm] = useState({
    batch_no: '', product_name: '', qc_type: 'final', inspector: '', wo_no: '', notes: '',
    parameters: [
      { name: 'Writing Smoothness', target: 'Grade A', actual: '', status: 'pass' },
      { name: 'Leakage Resistance', target: 'Zero leaks', actual: '', status: 'pass' },
      { name: 'Tip Durability (km)', target: '>2km', actual: '', status: 'pass' },
      { name: 'Packing Count', target: '10/box', actual: '', status: 'pass' },
    ],
  });

  const { user } = useAuth();
  const highlightId = useHighlight();
  const { prefill, hasDraft, restoreDraft, discardDraft, clearDraft } = useWorkflowDraft('quality', 'create_qc');

  const loadRecords = () => api.get('/quality').then(r => { setRecords(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { loadRecords(); }, []);

  useEffect(() => {
    if (!prefill) return;
    setForm(prev => ({
      ...prev,
      wo_id: prefill.wo_id || prev.wo_id,
      wo_no: prefill.wo_no || prev.wo_no,
      product_name: prefill.product_name || prev.product_name,
      order_no: prefill.order_no || prev.order_no,
    }));
  }, [prefill]);

  const loadCapas = async (qcId) => {
    try {
      const r = await api.get(`/capa/qc/${qcId}`);
      setCapas(r.data);
    } catch (e) { setCapas([]); }
  };

  const handleSelectRecord = (rec) => {
    setSelected(rec);
    setActiveTab('detail');
    if (rec.overall_status === 'hold') loadCapas(rec.id);
  };

  const handleVerdictChange = async (id, status) => {
    try {
      await api.put(`/quality/${id}/verdict`, { status, note: 'Updated via UI' });
      await loadRecords();
      if (selected?.id === id) setSelected(prev => ({ ...prev, overall_status: status }));
    } catch (e) { console.error(e); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/quality', form);
      await loadRecords();
      clearDraft();
      setShowAdd(false);
    } catch (e) { console.error(e); }
  };

  const handleCapaSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/capa', { ...capaForm, qc_record_id: selected.id, batch_no: selected.batch_no, product_name: selected.product_name });
      await loadCapas(selected.id);
      setShowCapa(false);
      setCapaForm({ root_cause_category: 'Process', root_cause_description: '', corrective_action: '', preventive_action: '', due_date: '', owner: '' });
    } catch (e) { console.error(e); }
  };

  const handleCapaStatus = async (cid, status) => {
    const note = status === 'closed' ? prompt('Closure note (required):') : '';
    if (status === 'closed' && !note) return;
    try {
      await api.put(`/capa/${cid}/status`, { status, closure_note: note || '' });
      await loadCapas(selected.id);
    } catch (e) { console.error(e); }
  };

  const updateParam = (i, field, val) => {
    setForm(prev => {
      const params = [...prev.parameters];
      params[i] = { ...params[i], [field]: val };
      return { ...prev, parameters: params };
    });
  };

  const stats = {
    pass: records.filter(r => r.overall_status === 'pass').length,
    hold: records.filter(r => r.overall_status === 'hold').length,
    fail: records.filter(r => r.overall_status === 'fail').length,
    total: records.length,
  };
  const passRate = stats.total > 0 ? ((stats.pass / stats.total) * 100).toFixed(0) : 0;

  return (
    <div data-testid="quality-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700, color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>Quality Control</h1>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Inspections · Certificates of Analysis · CAPA Workflow
          </div>
        </div>
        <button data-testid="add-qc-btn" onClick={() => setShowAdd(true)} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} weight="bold" /> NEW INSPECTION
        </button>
      </div>

      <div className="metrics-row" style={{ marginBottom: 20 }}>
        {[
          { label: 'Pass', val: stats.pass, color: 'hsl(197,100%,48%)', icon: CheckCircle },
          { label: 'Hold', val: stats.hold, color: 'hsl(43,96%,56%)', icon: Warning },
          { label: 'Fail', val: stats.fail, color: 'hsl(0,84%,60%)', icon: XCircle },
          { label: 'Pass Rate', val: passRate + '%', color: parseInt(passRate) >= 80 ? 'hsl(142,70%,45%)' : 'hsl(0,84%,60%)', icon: ClipboardText },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{s.label}</div>
              <s.icon size={14} color={s.color} />
            </div>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 26, fontWeight: 700, color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      <div className="split-view" style={{ gap: 1, height: 'calc(100vh - 280px)' }}>
        {/* List */}
        <div className="split-left" style={{ width: 360, background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)', overflowY: 'auto' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid hsl(0,0%,12%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Records ({records.length})
          </div>
          {loading ? <div style={{ padding: 20, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>Loading...</div>
          : records.map(r => {
            const isHighlighted = highlightId && (r.id === highlightId || r.batch_no === highlightId);
            return (
              <div key={r.id} data-testid={`qc-item-${r.batch_no}`}
                onClick={() => handleSelectRecord(r)}
                style={{ padding: '12px 14px', borderBottom: '1px solid hsl(0,0%,10%)', cursor: 'pointer',
                  background: selected?.id === r.id ? 'hsl(0,0%,7%)' : 'transparent',
                  borderLeft: isHighlighted ? '3px solid hsl(75,100%,50%)' :
                    r.overall_status === 'hold' ? '3px solid hsl(43,96%,56%)' :
                    r.overall_status === 'fail' ? '3px solid hsl(0,84%,60%)' :
                    r.overall_status === 'pass' ? '3px solid hsl(197,100%,48%)' : '3px solid transparent',
                  outline: isHighlighted ? '1px solid hsl(75,100%,50%)' : 'none',
                  transition: 'background 0.1s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(197,100%,48%)', fontWeight: 700, marginBottom: 2 }}>{r.batch_no}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,75%)', marginBottom: 4 }}>{r.product_name}</div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase' }}>{r.qc_type}</span>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)' }}>· {r.inspector}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <StatusChip status={r.overall_status} />
                    <WorkflowActions recordType="qc_record" record={r} userRole={user?.role} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail */}
        <div className="split-right" style={{ flex: 1, background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)', overflowY: 'auto' }}>
          {!selected ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'hsl(0,0%,30%)', gap: 12 }}>
              <ClipboardText size={40} />
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.15em' }}>Select a QC record</div>
            </div>
          ) : (
            <div data-testid="qc-detail" style={{ padding: 24 }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid hsl(0,0%,12%)' }}>
                <div>
                  <h2 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, fontWeight: 700, color: 'hsl(0,0%,95%)', textTransform: 'uppercase', marginBottom: 4 }}>{selected.batch_no}</h2>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,55%)' }}>{selected.product_name} · {selected.inspector}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <StatusChip status={selected.overall_status} />
                  <button onClick={() => printQCCOA(selected)} data-testid="print-coa-btn" className="btn-ghost" style={{ padding: '5px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Printer size={12} />Print COA
                  </button>
                  {selected.overall_status === 'hold' && (
                    <>
                      <button data-testid="release-btn" onClick={() => handleVerdictChange(selected.id, 'pass')}
                        style={{ padding: '5px 12px', background: 'hsl(197,100%,8%)', border: '1px solid hsl(197,100%,20%)', color: 'hsl(197,100%,48%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        RELEASE
                      </button>
                      <button data-testid="raise-capa-btn" onClick={() => setShowCapa(true)}
                        style={{ padding: '5px 12px', background: 'hsl(43,80%,10%)', border: '1px solid hsl(43,80%,25%)', color: 'hsl(43,96%,56%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <ListChecks size={12} />RAISE CAPA
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Tabs (Detail / CAPA) */}
              {selected.overall_status === 'hold' && (
                <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid hsl(0,0%,14%)' }}>
                  {['detail', 'capa'].map(t => (
                    <button key={t} onClick={() => { setActiveTab(t); if (t === 'capa') loadCapas(selected.id); }}
                      data-testid={`qc-tab-${t}`}
                      style={{ padding: '7px 16px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: activeTab === t ? 'hsl(197,100%,48%)' : 'hsl(0,0%,45%)', borderBottom: activeTab === t ? '2px solid hsl(197,100%,48%)' : '2px solid transparent', marginBottom: -1 }}>
                      {t === 'capa' ? `CAPA (${capas.length})` : 'INSPECTION'}
                    </button>
                  ))}
                </div>
              )}

              {activeTab === 'detail' && (
                <>
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>Test Parameters</div>
                    <div style={{ padding: '4px 0', borderBottom: '1px solid hsl(0,0%,12%)', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                      {['Parameter', 'Target', 'Actual', 'Result'].map(h => (
                        <div key={h} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.12em', flex: h === 'Parameter' ? 2 : 1, textAlign: h !== 'Parameter' ? 'center' : 'left' }}>{h}</div>
                      ))}
                    </div>
                    {selected.parameters?.map((p, i) => <ParamRow key={i} p={p} />)}
                  </div>
                  {selected.notes && (
                    <div style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,12%)', padding: 14 }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 }}>Notes</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,65%)', lineHeight: 1.6 }}>{selected.notes}</div>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'capa' && (
                <div>
                  {capas.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,35%)' }}>
                      No CAPA records for this batch. Click "RAISE CAPA" to create one.
                    </div>
                  ) : capas.map(c => (
                    <div key={c.id} data-testid={`capa-item-${c.capa_no}`}
                      style={{ background: 'hsl(0,0%,6%)', border: `1px solid ${CAPA_STATUSES[c.status] || 'hsl(0,0%,14%)'}30`, marginBottom: 12, padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(197,100%,48%)', fontWeight: 700, marginBottom: 2 }}>{c.capa_no}</div>
                          <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono', monospace", color: 'hsl(0,0%,40%)', textTransform: 'uppercase' }}>
                            {c.root_cause_category} · Raised by {c.created_by} · Due: {c.due_date || '—'}
                          </div>
                        </div>
                        <span style={{ fontSize: 9, padding: '3px 8px', background: `${CAPA_STATUSES[c.status]}22`, color: CAPA_STATUSES[c.status], fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.1em', border: `1px solid ${CAPA_STATUSES[c.status]}40` }}>
                          {c.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        {[
                          { label: 'Root Cause', val: c.root_cause_description },
                          { label: 'Owner', val: c.owner },
                          { label: 'Corrective Action', val: c.corrective_action },
                          { label: 'Preventive Action', val: c.preventive_action },
                        ].map(f => (
                          <div key={f.label}>
                            <div style={{ fontSize: 8, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>{f.label}</div>
                            <div style={{ fontSize: 10, color: 'hsl(0,0%,70%)', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.4 }}>{f.val || '—'}</div>
                          </div>
                        ))}
                      </div>
                      {c.closure_note && (
                        <div style={{ padding: '8px 10px', background: 'hsl(142,30%,8%)', border: '1px solid hsl(142,50%,18%)', marginBottom: 10 }}>
                          <div style={{ fontSize: 8, color: 'hsl(142,70%,45%)', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', marginBottom: 2 }}>Closure Note</div>
                          <div style={{ fontSize: 10, color: 'hsl(0,0%,65%)', fontFamily: "'JetBrains Mono', monospace" }}>{c.closure_note}</div>
                        </div>
                      )}
                      {c.status !== 'closed' && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          {c.status === 'open' && (
                            <button data-testid="capa-in-progress-btn" onClick={() => handleCapaStatus(c.id, 'in_progress')}
                              style={{ fontSize: 9, padding: '4px 10px', background: 'hsl(43,60%,10%)', border: '1px solid hsl(43,60%,25%)', color: 'hsl(43,96%,56%)', fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                              MARK IN PROGRESS
                            </button>
                          )}
                          <button data-testid="capa-close-btn" onClick={() => handleCapaStatus(c.id, 'closed')}
                            style={{ fontSize: 9, padding: '4px 10px', background: 'hsl(142,30%,8%)', border: '1px solid hsl(142,50%,20%)', color: 'hsl(142,70%,45%)', fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            CLOSE CAPA
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add QC Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,18%)', width: 600, maxHeight: '85vh', overflowY: 'auto', padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase', color: 'hsl(0,0%,95%)' }}>New Inspection</h3>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={18} /></button>
            </div>
            <DraftRestoreBanner hasDraft={hasDraft} onRestore={restoreDraft} onDiscard={discardDraft} />
            <form onSubmit={handleAdd}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {[
                  { key: 'batch_no', label: 'Batch No', req: true },
                  { key: 'qc_type', label: 'QC Type (incoming/in_process/final)' },
                  { key: 'product_name', label: 'Product Name', req: true },
                  { key: 'inspector', label: 'Inspector', req: true },
                  { key: 'notes', label: 'Notes' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={LBL}>{f.label}</label>
                    <input value={form[f.key]} required={f.req} data-testid={`qc-field-${f.key}`}
                      onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={INP} />
                  </div>
                ))}
                <div>
                  <label style={LBL}>Work Order No</label>
                  <SmartPicker
                    endpoint="/work-orders"
                    params={{ status: 'completed' }}
                    labelKey="wo_no"
                    searchKeys={['wo_no', 'product_name', 'order_no']}
                    placeholder="Search completed work orders..."
                    value={form.wo_no}
                    onSelect={(wo) => {
                      setForm(prev => ({
                        ...prev,
                        wo_id: wo.id,
                        wo_no: wo.wo_no,
                        product_name: wo.product_name || '',
                        order_no: wo.order_no || '',
                      }));
                    }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ ...LBL, marginBottom: 8 }}>Parameters</div>
                {form.parameters.map((p, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 100px', gap: 8, marginBottom: 8 }}>
                    {['name', 'target', 'actual'].map(field => (
                      <input key={field} value={p[field]} onChange={e => updateParam(i, field, e.target.value)}
                        placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                        style={{ background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)', padding: '6px 8px', color: 'hsl(0,0%,80%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, outline: 'none' }} />
                    ))}
                    <select value={p.status} onChange={e => updateParam(i, 'status', e.target.value)}
                      style={{ background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)', padding: '6px 8px', color: 'hsl(0,0%,80%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, outline: 'none' }}>
                      <option value="pass">pass</option>
                      <option value="fail">fail</option>
                      <option value="warning">warning</option>
                    </select>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost">Cancel</button>
                <button type="submit" data-testid="save-qc-btn" className="btn-primary">SAVE INSPECTION</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Raise CAPA Modal */}
      {showCapa && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(43,80%,25%)', width: 560, maxHeight: '88vh', overflowY: 'auto', padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div>
                <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase', color: 'hsl(43,96%,56%)', margin: 0 }}>Raise CAPA</h3>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,40%)', marginTop: 4 }}>{selected?.batch_no} · {selected?.product_name}</div>
              </div>
              <button onClick={() => setShowCapa(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={18} /></button>
            </div>
            <div style={{ marginBottom: 16, padding: '8px 12px', background: 'hsl(43,60%,8%)', border: '1px solid hsl(43,60%,20%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(43,96%,56%)', lineHeight: 1.5 }}>
              Corrective and Preventive Action (CAPA) is required for all QC Hold batches. Document root cause, corrective action, and preventive measures.
            </div>
            <form onSubmit={handleCapaSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={LBL}>Root Cause Category *</label>
                  <select required value={capaForm.root_cause_category} onChange={e => setCapaForm(p => ({ ...p, root_cause_category: e.target.value }))} style={{ ...INP }}>
                    {ROOT_CAUSE_CATS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={LBL}>Owner / Responsible *</label>
                  <input required value={capaForm.owner} onChange={e => setCapaForm(p => ({ ...p, owner: e.target.value }))} placeholder="Name / Department" style={INP} data-testid="capa-owner" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={LBL}>Root Cause Description *</label>
                  <textarea required rows={2} value={capaForm.root_cause_description} onChange={e => setCapaForm(p => ({ ...p, root_cause_description: e.target.value }))} placeholder="Describe the root cause in detail..." style={{ ...INP, resize: 'vertical' }} data-testid="capa-root-cause" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={LBL}>Corrective Action *</label>
                  <textarea required rows={2} value={capaForm.corrective_action} onChange={e => setCapaForm(p => ({ ...p, corrective_action: e.target.value }))} placeholder="Immediate action to fix the issue..." style={{ ...INP, resize: 'vertical' }} data-testid="capa-corrective" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={LBL}>Preventive Action *</label>
                  <textarea required rows={2} value={capaForm.preventive_action} onChange={e => setCapaForm(p => ({ ...p, preventive_action: e.target.value }))} placeholder="Long-term action to prevent recurrence..." style={{ ...INP, resize: 'vertical' }} data-testid="capa-preventive" />
                </div>
                <div>
                  <label style={LBL}>Due Date *</label>
                  <input required type="date" value={capaForm.due_date} onChange={e => setCapaForm(p => ({ ...p, due_date: e.target.value }))} style={INP} data-testid="capa-due-date" />
                </div>
              </div>
              <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCapa(false)} className="btn-ghost">Cancel</button>
                <button type="submit" data-testid="save-capa-btn" style={{ padding: '8px 20px', background: 'hsl(43,96%,56%)', border: 'none', color: '#000', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  RAISE CAPA
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
